import type { ResolvedConnectorDefinition } from '#layers/data-model/types/data-connectors/define';
import type { DataConnectorSummary, ExtensionPermission } from '#layers/extensions/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { isNonArrayRecord } from '#layers/base/public';
import { ConnectorSource, defineExtensionConnector } from '#layers/data-model/types/data-connectors/define';

/**
 * The form option holding the permissions the user approved after the Engine asked for more than the
 * manifest declares. They replace the declared set, because the Engine reports the whole set it needs.
 */
export const APPROVED_PERMISSIONS_OPTION = 'approvedPermissions';

export type ExtensionConnectorDefinition = Extract<ResolvedConnectorDefinition, { source: typeof ConnectorSource.Extension }>;

export function isExtensionConnector(connector: ResolvedConnectorDefinition): connector is ExtensionConnectorDefinition {
	return connector.source === ConnectorSource.Extension;
}

function parseConfiguration(configuration: unknown): Record<string, unknown> | undefined {
	if (typeof configuration !== 'string') return;

	try {
		const value: unknown = JSON.parse(configuration);
		if (!isNonArrayRecord(value)) return;
		return value;
	}
	catch {
		return undefined;
	}
}

export function createExtensionConnector({ id: provider, name, description, icon, iconForeground, iconBackground, extensionId, permissions }: DataConnectorSummary) {
	return defineExtensionConnector<Record<string, unknown> & { [APPROVED_PERMISSIONS_OPTION]?: ExtensionPermission[] }>({
		id: provider,
		provider,
		name,
		description: description ?? '',
		icon,
		iconForeground,
		iconBackground,
		keywords: [provider, ...(extensionId ? [extensionId] : [])],
		options: [{
			name: 'configuration',
			label: '$t:extensions.configuration',
			description: '$t:extensions.configuration_description',
			block: BuiltinBlockIds.InputCode,
			default: () => '{}',
		}],
		validateOptions(values) {
			return parseConfiguration(values.configuration)
				? []
				: [{ name: 'configuration', message: '$t:extensions.invalid_configuration' }];
		},
		transformOptions(values) {
			// Transform also runs while editing; validation blocks submitting incomplete JSON.
			return {
				config: parseConfiguration(values.configuration) ?? {},
				permissions: values[APPROVED_PERMISSIONS_OPTION] ?? permissions,
			};
		},
	});
}
